import { useState, useEffect } from "react";
import SEO from "../seo";
import { APP_DATA } from "../constant_data";
const FreeTrial = () => {
  const [activeSection, setActiveSection] = useState("whats-included");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "whats-included",
        "whats-restricted",
        "trial-duration",
        "after-trial",
        "faqs",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="xPage Free Trial – Explore the Full Platform for $0"
        description="Start your xPage free trial and access all core features for 3 days. Build, customize, and preview your landing pages without entering credit card details."
        keywords="xPage free trial, landing page builder, AI tools, online store builder, trial features"
        canonical="https://help.xpage.ai/help/pricing-plans/Free%20Trial"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title Group */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">
            Free Trial: Get Started on xPage for $0
          </h1>
          <p className="text-sm text-systemSubTitle mt-1">
            xPage Free Trial lets you explore the full power of our platform
            before making any payment. You can build, customize, and preview
            your landing page completely free.
          </p>
        </div>

        {/* What's Included Section */}
        <div id="whats-included" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-2">
            What's included in the free trial?
          </h2>
          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <p className="text-systemLabel text-sm">
              xPage free trial gives you {APP_DATA.FREE_TRIAL_DAYS} days of
              access to all core features, no credit card required. You can
              create unlimited landing pages. Mobile-first templates, advanced
              analytics, referral tools, and features like bundles and offers
              are all included during your trial period.
            </p>
          </div>
        </div>

        {/* What's Restricted Section */}
        <div id="whats-restricted" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-2">
            What's restricted during the trial?
          </h2>
          <p className="text-systemLabel text-sm mt-1 mb-3">
            While most of the platform is available, some key functions remain
            locked until you upgrade. You won't be able to:
          </p>

          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <ul className="space-y-2">
              <li className="flex items-center">
                <div className="bg-core rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-globe text-white text-xs"></i>
                </div>
                <span className="text-systemLabel text-sm">
                  Publish your store or landing page
                </span>
              </li>
              <li className="flex items-center">
                <div className="bg-core rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-link text-white text-xs"></i>
                </div>
                <span className="text-systemLabel text-sm">
                  Connect a custom domain
                </span>
              </li>
              <li className="flex items-center">
                <div className="bg-core rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-credit-card text-white text-xs"></i>
                </div>
                <span className="text-systemLabel text-sm">
                  Activate payment methods like Stripe or PayPal
                </span>
              </li>
              <li className="flex items-center">
                <div className="bg-core rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-shopping-cart text-white text-xs"></i>
                </div>
                <span className="text-systemLabel text-sm">
                  Access the admin orders panel (requires a live store)
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Trial Duration Section */}
        <div id="trial-duration" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-2">
            How long does the trial last?
          </h2>
          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <p className="text-systemLabel text-sm">
              The trial lasts for {APP_DATA.FREE_TRIAL_DAYS} days from the
              moment you sign up. No credit card is required to begin, and your
              countdown starts immediately after registration. If you need more
              time to explore, you can reach out to support to request a trial
              extension.
            </p>
          </div>
        </div>

        {/* After Trial Section */}
        <div id="after-trial" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-2">
            What happens after the trial?
          </h2>
          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <p className="text-systemLabel text-sm">
              At the end of your free trial, your landing pages will be saved as
              a draft. You won't lose any of your content, products, or
              settings. When you're ready to launch, simply choose a plan to
              unlock landing page publishing, domain connection, and payment
              setup. Your landing page will go live instantly with everything
              you've already built.
            </p>
          </div>
        </div>

        {/* FAQs Section */}
        <div id="faqs" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">FAQs</h2>

          <div className="space-y-3">
            <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
              <h3 className="text-systemTitle text-sm font-semibold mb-2">
                Do I need to add my credit card to start?
              </h3>
              <p className="text-systemLabel text-sm">
                No. You can begin building your landing page with zero
                commitment. No credit card is required during the trial.
              </p>
            </div>

            <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
              <h3 className="text-systemTitle text-sm font-semibold mb-2">
                Will my store disappear after the trial ends?
              </h3>
              <p className="text-systemLabel text-sm">
                No. Your landing page remains in draft mode until you subscribe.
                You can resume editing anytime.
              </p>
            </div>

            <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
              <h3 className="text-systemTitle text-sm font-semibold mb-2">
                Can I launch my landing page during the trial?
              </h3>
              <p className="text-systemLabel text-sm">
                You can build and preview it, but you'll need to upgrade to a
                paid plan to go live and accept payments. (the landing page will
                have a password when published without subscription).
              </p>
            </div>

            <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
              <h3 className="text-systemTitle text-sm font-semibold mb-2">
                Are all features available during the trial?
              </h3>
              <p className="text-systemLabel text-sm">
                Yes! You get full access to the builder, AI tools, analytics,
                and other features, except publishing and payment integration.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar - Same design as previous components */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#whats-included"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "whats-included"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                What's Included
              </a>
            </li>
            <li>
              <a
                href="#whats-restricted"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "whats-restricted"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                What's Restricted
              </a>
            </li>
            <li>
              <a
                href="#trial-duration"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "trial-duration"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Trial Duration
              </a>
            </li>
            <li>
              <a
                href="#after-trial"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "after-trial"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                After Trial
              </a>
            </li>
            <li>
              <a
                href="#faqs"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "faqs"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                FAQs
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FreeTrial;
